import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './input';
import { MapPin, Loader2, X } from 'lucide-react';

// Debounce helper
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

/**
 * Address Autocomplete using OpenStreetMap Nominatim (FREE, no API key required)
 */
export const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Start typing an address...",
  className = "",
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  
  const debouncedSearch = useDebounce(inputValue, 300);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);
  
  // Search via backend proxy (avoids CORS issues with Nominatim)
  const searchAddresses = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }
    
    setIsLoading(true);
    try {
      // Use backend proxy to avoid CORS issues
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${backendUrl}/api/geocode/search?q=${encodeURIComponent(query)}&limit=5`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowDropdown(data.length > 0);
      }
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Trigger search when debounced value changes
  useEffect(() => {
    if (debouncedSearch) {
      searchAddresses(debouncedSearch);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [debouncedSearch, searchAddresses]);
  
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };
  
  const handleSelectAddress = (place) => {
    const address = place.address || {};
    
    // Build street address
    let street = '';
    if (address.house_number) street += address.house_number + ' ';
    if (address.road) street += address.road;
    if (!street && address.neighbourhood) street = address.neighbourhood;
    
    // Get city (Nominatim uses various fields)
    const city = address.city || address.town || address.village || address.municipality || address.county || '';
    
    // Get state
    const state = address.state || '';
    
    // Get zip
    const zip = address.postcode || '';
    
    const addressData = {
      formatted_address: place.display_name,
      street: street.trim(),
      city: city,
      state: getStateAbbreviation(state),
      zip: zip,
      country: address.country_code?.toUpperCase() || 'US',
      lat: parseFloat(place.lat),
      lng: parseFloat(place.lon)
    };
    
    // Update input with street address or full address
    const displayValue = street.trim() || place.display_name.split(',')[0];
    setInputValue(displayValue);
    onChange(displayValue);
    
    // Callback with full parsed address
    if (onAddressSelect) {
      onAddressSelect(addressData);
    }
    
    setShowDropdown(false);
    setSuggestions([]);
  };
  
  const clearInput = () => {
    setInputValue('');
    onChange('');
    setSuggestions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };
  
  // Format suggestion for display
  const formatSuggestion = (place) => {
    const parts = place.display_name.split(',');
    const main = parts[0];
    const secondary = parts.slice(1, 3).join(',').trim();
    return { main, secondary };
  };
  
  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => suggestions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          className={`pl-10 pr-10 bg-slate-950 border-slate-800 text-white ${className}`}
          disabled={disabled}
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={clearInput}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      
      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-700 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((place, index) => {
            const { main, secondary } = formatSuggestion(place);
            return (
              <button
                key={place.place_id || index}
                type="button"
                onClick={() => handleSelectAddress(place)}
                className="w-full px-4 py-3 text-left hover:bg-slate-800 border-b border-slate-700 last:border-b-0 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-white text-sm font-medium">{main}</div>
                    <div className="text-slate-400 text-xs">{secondary}</div>
                  </div>
                </div>
              </button>
            );
          })}
          <div className="px-4 py-2 text-xs text-slate-500 bg-slate-800/50">
            Powered by OpenStreetMap
          </div>
        </div>
      )}
    </div>
  );
};

// US State abbreviation helper
const getStateAbbreviation = (stateName) => {
  const states = {
    'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR',
    'california': 'CA', 'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE',
    'florida': 'FL', 'georgia': 'GA', 'hawaii': 'HI', 'idaho': 'ID',
    'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA', 'kansas': 'KS',
    'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
    'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS',
    'missouri': 'MO', 'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV',
    'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
    'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH', 'oklahoma': 'OK',
    'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
    'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT',
    'vermont': 'VT', 'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV',
    'wisconsin': 'WI', 'wyoming': 'WY', 'district of columbia': 'DC'
  };
  
  if (!stateName) return '';
  
  // If already abbreviated (2 chars), return as-is
  if (stateName.length === 2) return stateName.toUpperCase();
  
  // Look up full name
  return states[stateName.toLowerCase()] || stateName;
};

// Simple fallback input (same interface)
export const AddressInput = ({
  value,
  onChange,
  placeholder = "Enter address",
  className = "",
  disabled = false
}) => {
  return (
    <div className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`pl-10 bg-slate-950 border-slate-800 text-white ${className}`}
        disabled={disabled}
      />
    </div>
  );
};

export default AddressAutocomplete;
